import { useState, useEffect } from "react";
import {
  FaPlus,
  FaSearch,
  FaSpinner,
  FaEnvelope,
  FaClock,
  FaTimes,
} from "react-icons/fa";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import { useNotifications } from "../../../context/NotificationContext";
import { supabase } from "../../../supabaseClient";
import { fetchUsersByIds } from "../../../utils/supabase";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import {
  SoftButton,
  ConfirmDialog,
  formatShortDate,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import EditMemberRoleModal from "./EditMemberRoleModal";
import InviteMemberModal from "./InviteMemberModal";
import RemoveMemberButton from "./RemoveMemberButton";

// Note: Rate limiting is now handled server-side

const MemberList = () => {
  const { currentOrganization } = useOrganization();
  const permissions = usePermissions();
  const { refreshInvites } = useNotifications();
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [processingInvites, setProcessingInvites] = useState(new Set());
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    inviteId: null,
    inviteEmail: "",
  });

  const fetchMembers = async () => {
    if (!currentOrganization) {
      setMembers([]);
      setLoading(false);
      return;
    }

    try {
      // First get the organization members with their roles (including members without roles)
      const { data: memberData, error: memberError } = await supabase
        .from("organizationMembers")
        .select(
          `
          id,
          userID,
          status,
          createdAt,
          memberRoles:organizationMemberRoles (
            id,
            roleID,
            isDeleted,
            role:organizationRoles (
              id,
              name,
              description,
              permissions,
              rank
            )
          )
        `
        )
        .eq("organizationID", currentOrganization.id)
        .eq("status", "active");

      if (memberError) throw memberError;

      // Then get the user details for each member
      const userIds = memberData.map((member) => member.userID);

      const { data: userData, error: userError } = await fetchUsersByIds(
        userIds
      );

      if (userError) throw userError;

      // Combine the data and filter out soft-deleted roles
      const combinedData = memberData.map((member) => ({
        ...member,
        user: userData.find((user) => user.id === member.userID),
        organizationID: currentOrganization.id,
        memberRoles:
          member.memberRoles?.filter((role) => !role.isDeleted) || [],
      }));

      setMembers(combinedData);
    } catch (err) {
      console.error("Error fetching members:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    if (!currentOrganization) {
      setPendingInvites([]);
      return;
    }

    try {
      const { data: invites, error } = await supabase
        .from("organizationInvites")
        .select(
          `
          id,
          email,
          status,
          createdAt,
          expiredAt,
          invitedBy,
          roleIDs
        `
        )
        .eq("organizationID", currentOrganization.id)
        .eq("status", "pending")
        .gt("expiredAt", new Date().toISOString())
        .order("createdAt", { ascending: false });

      if (error) throw error;

      // If we have invites, get the user details for invitedBy
      if (invites && invites.length > 0) {
        const inviterIds = invites
          .map((invite) => invite.invitedBy)
          .filter(Boolean);

        if (inviterIds.length > 0) {
          const { data: inviterUsers, error: userError } =
            await fetchUsersByIds(inviterIds);

          if (!userError && inviterUsers) {
            // Combine the data
            const invitesWithUsers = invites.map((invite) => ({
              ...invite,
              invitedByUser: inviterUsers.find(
                (user) => user.id === invite.invitedBy
              ),
            }));
            setPendingInvites(invitesWithUsers);
            return;
          }
        }
      }

      // Fallback: just set the invites without user details
      setPendingInvites(invites || []);
    } catch (err) {
      console.error("Error fetching pending invites:", err);
      setPendingInvites([]);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchPendingInvites();
  }, [currentOrganization]);

  const handleEditClick = (member) => {
    setSelectedMember(member);
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    // Refresh the members list
    fetchMembers();
  };

  const handleInviteSent = () => {
    // Refresh the pending invites list
    fetchPendingInvites();
    refreshInvites(); // Refresh notifications
  };

  const handleCancelInvite = (inviteId, inviteEmail) => {
    setConfirmDialog({
      isOpen: true,
      inviteId,
      inviteEmail,
    });
  };

  const confirmCancelInvite = async () => {
    const { inviteId } = confirmDialog;
    setProcessingInvites((prev) => new Set(prev).add(inviteId));

    try {
      const { error } = await supabase
        .from("organizationInvites")
        .update({
          status: "cancelled",
        })
        .eq("id", inviteId);

      if (error) throw error;

      notify.success("Invitation cancelled successfully");
      setErrorMessage(""); // Clear any previous errors
      fetchPendingInvites();
      refreshInvites(); // Refresh notifications
    } catch (error) {
      console.error("Error cancelling invite:", error);
      notify.error("Failed to cancel invitation");
      setErrorMessage("Failed to cancel invitation");
    } finally {
      setProcessingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(inviteId);
        return newSet;
      });
      setConfirmDialog({
        isOpen: false,
        inviteId: null,
        inviteEmail: "",
      });
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      inviteId: null,
      inviteEmail: "",
    });
  };

  const handleRemoveMember = async (member) => {
    if (!permissions.canRemoveMember(member)) {
      setErrorMessage("You don't have permission to remove this member");
      return;
    }

    try {
      const { error } = await supabase
        .from("organizationMembers")
        .update({
          status: "inactive",
          updatedAt: new Date().toISOString(),
          updatedBy: (await supabase.auth.getUser()).data.user.id,
        })
        .eq("id", member.id);

      if (error) throw error;
      fetchMembers();
    } catch (error) {
      console.error("Error removing member:", error);
      setErrorMessage("Failed to remove member");
    }
  };

  const filteredMembers = members.filter((member) => {
    const fullName = member.user?.full_name?.toLowerCase() || "";
    const email = member.user?.email?.toLowerCase() || "";
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const getRoleBadgeColor = () =>
    "bg-brand-charcoal text-brand-cream rounded-lg";

  // Use formatShortDate from design standards (now handles ISO timestamps)
  const formatDate = (dateString) => formatShortDate(dateString);

  if (loading) {
    return (
      <div className="space-y-4" role="status" aria-label="Loading members">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">Error loading members: {error}</div>
    );
  }

  return (
    <div className="space-y-6">
      {errorMessage ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      ) : null}
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Members
            </h2>
            <p className="text-sm text-brand-mist mt-1">
              Roles and invitations for this organization.
            </p>
          </div>
          {permissions?.canManageMembers && (
            <SecondaryButton
              type="button"
              onClick={() => setIsInviteModalOpen(true)}
              className="!px-3 !py-2 text-sm"
              icon={FaPlus}
            >
              Invite Member
            </SecondaryButton>
          )}
        </div>
      </header>

      {/* Pending Invitations Section */}
      {pendingInvites.length > 0 && (
        <div className="card-board overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-brand-charcoal/10">
            <h3 className="heading-display text-lg flex items-center">
              <FaEnvelope className="mr-2 text-brand-muted" />
              Pending Invitations ({pendingInvites.length})
            </h3>
          </div>
          <ul className="divide-y divide-brand-charcoal/10">
            {pendingInvites.map((invite) => (
              <li key={invite.id} className="px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-brand-cream flex items-center justify-center">
                        <FaClock className="h-5 w-5 text-brand-muted" />
                      </div>
                    </div>
                    <div className="ml-4 min-w-0 break-all">
                      <div className="text-sm font-medium text-brand-charcoal">
                        {invite.email}
                      </div>
                      <div className="text-sm text-brand-muted">
                        Invited by{" "}
                        {invite.invitedByUser?.full_name ||
                          invite.invitedByUser?.email ||
                          "Unknown user"}{" "}
                        on {formatDate(invite.createdAt)}
                      </div>
                      <div className="text-xs text-brand-mist">
                        Expires {formatDate(invite.expiredAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SoftButton
                      onClick={() =>
                        handleCancelInvite(invite.id, invite.email)
                      }
                      disabled={processingInvites.has(invite.id)}
                      color="red"
                      size="sm"
                      iconLeft={
                        processingInvites.has(invite.id) ? (
                          <FaSpinner className="animate-spin h-3 w-3" />
                        ) : (
                          <FaTimes className="h-3 w-3" />
                        )
                      }
                    >
                      Cancel
                    </SoftButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="relative rounded-lg">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaSearch className="h-5 w-5 text-brand-muted" />
        </div>
        <input
          type="text"
          className="focus:ring-brand-gold-ink focus:border-brand-gold-ink block w-full min-h-11 pl-10 py-2.5 sm:text-sm border border-brand-charcoal/20 rounded-lg"
          placeholder="Search members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="card-board overflow-hidden">
        <ul className="divide-y divide-brand-charcoal/10">
          {filteredMembers.map((member) => (
            <li key={member.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-brand-cream flex items-center justify-center">
                        <span className="text-lg font-medium text-brand-muted">
                          {member.user?.full_name?.charAt(0) ||
                            member.user?.email?.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 min-w-0 break-all">
                      <div className="text-sm font-medium text-brand-charcoal">
                        {member.user?.full_name || member.user?.email}
                      </div>
                      <div className="text-sm text-brand-muted">
                        {member.user?.email}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-start sm:items-end gap-3 min-w-0">
                    {/* Role chips */}
                    <div className="flex flex-wrap gap-2">
                      {member.memberRoles?.length > 0 ? (
                        member.memberRoles.map((memberRole) => (
                          <span
                            key={memberRole.id}
                            className={`inline-flex items-center max-w-full break-words px-2.5 py-0.5 text-xs font-medium ${getRoleBadgeColor(
                              memberRole.role.name.toLowerCase()
                            )}`}
                          >
                            {memberRole.role.name}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center max-w-full break-words px-2.5 py-0.5 text-xs font-medium bg-brand-charcoal text-brand-cream rounded-lg">
                          Member
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      {permissions.canManageMembers &&
                        permissions.canModifyMember(member.memberRoles) && (
                          <SoftButton
                            color="cream"
                            size="sm"
                            type="button"
                            onClick={() => handleEditClick(member)}
                          >
                            Edit
                          </SoftButton>
                        )}
                      {permissions.canRemoveMember(member) && (
                        <RemoveMemberButton
                          member={member}
                          onRemove={handleRemoveMember}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {filteredMembers.length === 0 && (
            <li className="px-4 py-4 sm:px-6 text-center text-brand-muted">
              No members found
            </li>
          )}
        </ul>
      </div>

      {/* Modals */}
      <EditMemberRoleModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        member={selectedMember}
        onUpdate={handleUpdate}
      />

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInviteSent={handleInviteSent}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmCancelInvite}
        title="Cancel Invitation"
        message={`Are you sure you want to cancel the invitation for ${confirmDialog.inviteEmail}? This action cannot be undone.`}
        confirmText="Cancel Invitation"
        cancelText="Keep Invitation"
        variant="danger"
        loading={processingInvites.has(confirmDialog.inviteId)}
      />
    </div>
  );
};

export default MemberList;
